import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Upload,
  FileText,
  Brain,
  Target,
  TrendingUp,
  Clock,
  Star,
  Calendar,
  Plus,
  ArrowRight,
  Edit
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { NameModal } from '../components/ui/NameModal';

export const Dashboard: React.FC = () => {
  const { userProfile } = useAuth();

  // State for real recent activities
  const [recentActivities, setRecentActivities] = useState<any[]>([]);
  
  // State for name modal
  const [showNameModal, setShowNameModal] = useState(false);
  const [hasDismissedModal, setHasDismissedModal] = useState(false);

  useEffect(() => {
    // Read from localStorage
    const docs = JSON.parse(localStorage.getItem('ecstatic_uploaded_documents') || '[]');
    const flashcards = JSON.parse(localStorage.getItem('ecstatic_uploaded_flashcards') || '[]');
    const quizzes = JSON.parse(localStorage.getItem('ecstatic_uploaded_quizzes') || '[]');

    // Map to unified activity format
    const docActivities = docs.map((doc: any) => ({
      type: 'document',
      title: doc.fileName,
      subtitle: 'Uploaded and processed',
      time: doc.processedAt,
      icon: FileText
    }));
    const flashcardActivities = flashcards.map((fc: any) => ({
      type: 'flashcard',
      title: `Studied ${fc.count} flashcards`,
      subtitle: 'Flashcard session completed',
      time: fc.completedAt,
      icon: Star
    }));
    const quizActivities = quizzes.map((qz: any) => ({
      type: 'quiz',
      title: `Quiz - ${qz.score}%`,
      subtitle: `${qz.correctAnswers} out of ${qz.totalQuestions} correct`,
      time: qz.completedAt,
      icon: Brain
    }));

    // Combine and sort by time (most recent first)
    const allActivities = [...docActivities, ...flashcardActivities, ...quizActivities].sort((a, b) => {
      return new Date(b.time).getTime() - new Date(a.time).getTime();
    });
    setRecentActivities(allActivities);
  }, []);

  // Check if user needs to provide their name
  useEffect(() => {
    if (userProfile && (!userProfile.displayName || userProfile.displayName.trim() === '') && !hasDismissedModal) {
      setShowNameModal(true);
    }
  }, [userProfile, hasDismissedModal]);

  const stats = [
    {
      label: 'Documents',
      value: userProfile?.studyStats?.documentsUploaded || 0,
      icon: FileText,
      color: 'text-primary-600',
      bgColor: 'bg-primary-50 dark:bg-primary-900/20'
    },
    {
      label: 'Quizzes',
      value: userProfile?.studyStats?.quizzesCompleted || 0,
      icon: Brain,
      color: 'text-secondary-600',
      bgColor: 'bg-secondary-50 dark:bg-secondary-900/20'
    },
    {
      label: 'Flashcards',
      value: userProfile?.studyStats?.flashcardsCreated || 0,
      icon: Star,
      color: 'text-accent-600',
      bgColor: 'bg-accent-50 dark:bg-accent-900/20'
    },
    {
      label: 'Study Time',
      value: `${userProfile?.studyStats?.studyMinutes || 0}m`,
      icon: TrendingUp,
      color: 'text-success-600',
      bgColor: 'bg-success-50 dark:bg-success-900/20'
    }
  ];

  const quickActions = [
    {
      title: 'Upload Document',
      description: 'Add new study material',
      icon: Upload,
      href: '/upload',
      color: 'bg-primary-600 hover:bg-primary-700'
    },
    {
      title: 'Study Flashcards',
      description: 'Review your flashcards',
      icon: Star,
      href: '/study',
      color: 'bg-secondary-600 hover:bg-secondary-700'
    },
    {
      title: 'Take Quiz',
      description: 'Test your knowledge',
      icon: Brain,
      href: '/study',
      color: 'bg-accent-600 hover:bg-accent-700'
    }
  ];

  const formatLastActive = (lastActive: string) => {
    try {
      const date = new Date(lastActive);
      const today = new Date();
      const diffTime = Math.abs(today.getTime() - date.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays === 1) return 'Today';
      if (diffDays === 2) return 'Yesterday';
      return date.toLocaleDateString();
    } catch {
      return 'Today';
    }
  };

  return (
    <div className="h-full space-y-4">
      {/* Header - Compact */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 sm:gap-0">
        <div className="flex-1 min-w-0">
          <h1 className="text-lg sm:text-2xl font-heading font-bold text-gray-900 dark:text-white break-words">
            Welcome back, {userProfile?.displayName?.trim() || 'Student'}! <span role='img' aria-label='wave'>👋</span>
          </h1>
          <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1">
            Ready to continue your learning journey?
          </p>
        </div>
        <div className="flex flex-row items-center space-x-2 sm:space-x-3 mt-2 sm:mt-0">
          <div className="flex items-center space-x-1 sm:space-x-2 text-[11px] sm:text-xs text-gray-500 dark:text-gray-400">
            <Clock className="h-3 w-3" />
            <span>
              Last active: {userProfile?.studyStats?.lastActive 
                ? formatLastActive(userProfile.studyStats.lastActive) 
                : 'Today'}
            </span>
          </div>
          {userProfile?.displayName?.trim() && (
            <button
              onClick={() => {
                setHasDismissedModal(false);
                setShowNameModal(true);
              }}
              className="flex items-center space-x-1 text-[11px] sm:text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors px-2 py-1 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              style={{ minHeight: '32px', minWidth: '44px' }}
            >
              <Edit className="h-3 w-3" />
              <span>Edit</span>
            </button>
          )}
        </div>
      </div>

      {/* Stats Grid - Compact */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {stats.map((stat, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
            className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                  {stat.label}
                </p>
                <p className="text-lg font-bold text-gray-900 dark:text-white mt-1">
                  {stat.value}
                </p>
              </div>
              <div className={`p-2 rounded-lg ${stat.bgColor}`}>
                <stat.icon className={`h-4 w-4 ${stat.color}`} />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Quick Actions - Compact */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
          Quick Actions
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {quickActions.map((action, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <Link
                to={action.href}
                className={`block p-3 rounded-lg text-white transition-colors ${action.color}`}
              >
                <div className="flex items-center space-x-2">
                  <action.icon className="h-4 w-4" />
                  <div className="flex-1">
                    <h3 className="text-sm font-medium">{action.title}</h3>
                    <p className="text-xs opacity-90">{action.description}</p>
                  </div>
                  <ArrowRight className="h-3 w-3" />
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Recent Activity and Study Progress - Side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Recent Activity - Compact */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Recent Activity
            </h2>
            <span className="text-primary-600 text-xs font-medium">
              Local storage
            </span>
          </div>
          <div className="space-y-3">
            {recentActivities.length === 0 ? (
              <div className="text-center py-6 text-gray-500 dark:text-gray-400">
                <p className="text-sm">No recent activity yet.</p>
                <p className="text-xs mt-1">Upload a document or start studying!</p>
              </div>
            ) : (
              recentActivities.slice(0, 4).map((activity, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  className="flex items-start space-x-2"
                >
                  <div className="p-1.5 bg-gray-100 dark:bg-gray-700 rounded-lg">
                    <activity.icon className="h-3 w-3 text-gray-600 dark:text-gray-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-900 dark:text-white truncate">
                      {activity.title}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {activity.subtitle}
                    </p>
                  </div>
                  <span className="text-xs text-gray-400 dark:text-gray-500">
                    {activity.time ? new Date(activity.time).toLocaleDateString() : ''}
                  </span>
                </motion.div>
              ))
            )}
          </div>
        </div>

        {/* Study Progress - Compact */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
            Study Progress
          </h2>
          <div className="space-y-3">
            <div>
              <div className="flex justify-between items-center mb-1">
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                  Documents
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {userProfile?.studyStats?.documentsUploaded || 0}/10
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                <div 
                  className="bg-primary-600 h-1.5 rounded-full transition-all duration-300"
                  style={{ width: `${Math.min(((userProfile?.studyStats?.documentsUploaded || 0) / 10) * 100, 100)}%` }}
                ></div>
              </div>
            </div>
            
            <div>
              <div className="flex justify-between items-center mb-1">
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                  Study Time
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {userProfile?.studyStats?.studyMinutes || 0}/1200m
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                <div 
                  className="bg-secondary-600 h-1.5 rounded-full transition-all duration-300"
                  style={{ width: `${Math.min(((userProfile?.studyStats?.studyMinutes || 0) / 1200) * 100, 100)}%` }}
                ></div>
              </div>
            </div>
            
            <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-2 text-xs text-gray-600 dark:text-gray-400">
                <Calendar className="h-3 w-3" />
                <span>Data stored locally</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Getting Started Section (for new users) - Compact */}
      {(!userProfile?.studyStats?.documentsUploaded || userProfile.studyStats.documentsUploaded === 0) && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-gradient-to-r from-primary-50 to-secondary-50 dark:from-primary-900/20 dark:to-secondary-900/20 rounded-lg p-6 border border-primary-200 dark:border-primary-800"
        >
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Get Started with EcstaticLearn
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Upload your first document to begin creating AI-powered study materials
            </p>
            <Link
              to="/upload"
              className="inline-flex items-center space-x-2 bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
            >
              <Plus className="h-4 w-4" />
              <span>Upload Your First Document</span>
            </Link>
          </div>
        </motion.div>
      )}
      
      {/* Name Modal */}
      <NameModal 
        isOpen={showNameModal} 
        onClose={() => {
          setShowNameModal(false);
          setHasDismissedModal(true);
        }}
        isEditing={hasDismissedModal}
      />
    </div>
  );
};